// Pending-invite pickup (docs/06-clients.md §4): a Web Push notification tap
// opens/focuses the PWA, which must then pick up the invite the server is
// holding (45 s of ring time). The actual invite frame arrives over the WSS
// once the leader tab's signaling client connects — pickup therefore means:
//   1. learn the callId (deep-link #fragment on a cold open, or a SW
//      PENDING_INVITE message when a window already existed), and
//   2. make sure the engine is running so the queued `call.invite` is
//      delivered and rings.
// The fragment is stripped from the URL immediately so it never lingers in
// history or gets copied around.
//
// The fragment may additionally carry the user's decision from a NATIVE
// accept surface (Android IncomingCallActivity / notification action):
// `#invite=<callId>&action=accept`. The user already accepted there — the
// React incoming-call screen must not ask again; installAutoAccept() drives
// engine.accept() the moment the matching invite is ringing.

import { TIMEOUTS } from '@merkury/protocol';
import { Store, type ReadableStore } from '@merkury/brain/call/store.js';
import type { CallEngineState } from '@merkury/brain/call/types.js';
import {
  INVITE_FRAGMENT_PREFIX,
  isUuid,
  parsePendingDeclineMessage,
  parsePendingInviteMessage,
} from '../sw/messages.js';

export type PendingInviteAction = 'accept' | 'decline' | null;

export interface PendingInvite {
  callId: string;
  /** ms epoch when the pickup signal arrived (UI can age it out vs 45 s). */
  receivedAt: number;
  /** Decision already made on a native surface; 'accept' → skip the JS ask. */
  action: PendingInviteAction;
}

/**
 * Parse an invite hash, tolerating the native-decision suffix:
 * `#invite=<uuid>`, `#invite=<uuid>&action=accept` and
 * `#invite=<uuid>&action=decline` all resolve; anything else (bad uuid, other
 * fragments) is null. Exported so the shapes the Android shell emits
 * (DeepLink.java inviteHash/inviteAcceptHash) and the SW's decline fallback link
 * stay contract-tested.
 */
export function parseInviteHash(hash: string): { callId: string; action: PendingInviteAction } | null {
  if (!hash.startsWith(INVITE_FRAGMENT_PREFIX)) return null;
  const [callId, ...params] = hash.slice(INVITE_FRAGMENT_PREFIX.length).split('&');
  if (!isUuid(callId)) return null;
  const action: PendingInviteAction = params.includes('action=accept')
    ? 'accept'
    : params.includes('action=decline')
      ? 'decline'
      : null;
  return { callId, action };
}

export interface PendingInviteDeps {
  /** Ensure the call engine / signaling client is running (idempotent). */
  ensureEngineStarted: () => void;
  now?: () => number;
}

export class PendingInvitePickup {
  readonly state: ReadableStore<PendingInvite | null> & Store<PendingInvite | null>;
  private readonly deps: PendingInviteDeps;

  constructor(deps: PendingInviteDeps) {
    this.deps = deps;
    this.state = new Store<PendingInvite | null>(null);
  }

  /**
   * Cold-open path: consume `#invite=<callId>[&action=accept]` from the
   * location, if present. Strips the fragment via history.replaceState.
   * Returns the callId or null.
   */
  consumeLocation(
    location: { hash: string; pathname: string; search: string },
    history: { replaceState(data: unknown, unused: string, url?: string): void },
  ): string | null {
    const parsed = parseInviteHash(location.hash);
    if (parsed === null) return null;
    history.replaceState(null, '', location.pathname + location.search);
    this.pickup(parsed.callId, parsed.action);
    return parsed.callId;
  }

  /**
   * Warm path: a message posted by the SW on a notification gesture —
   * PENDING_INVITE (plain tap or Accept action) or PENDING_DECLINE (Decline
   * action, delegated here because this page holds the live authenticated
   * socket). Returns true when consumed.
   */
  handleSwMessage(data: unknown): boolean {
    const invite = parsePendingInviteMessage(data);
    if (invite !== null) {
      this.pickup(invite.callId, invite.action);
      return true;
    }
    const decline = parsePendingDeclineMessage(data);
    if (decline !== null) {
      this.pickup(decline.callId, 'decline');
      return true;
    }
    return false;
  }

  /** The ringing UI consumed (or dismissed) the pending invite. */
  clear(): void {
    this.state.set(null);
  }

  /**
   * A local ring surface decided for a pending invite that has NOT rung yet
   * (provisional decline): record the decision so installAutoAccept completes
   * it against the MATCHING invite the moment it arrives — dismissing locally
   * alone would let the call re-ring when the frame lands. No-op unless the
   * callId matches the current pending invite.
   */
  decide(callId: string, action: 'accept' | 'decline'): void {
    const cur = this.state.get();
    if (cur === null || cur.callId !== callId) return;
    this.state.set({ ...cur, action });
  }

  private pickup(callId: string, action: PendingInviteAction): void {
    this.state.set({ callId, receivedAt: this.deps.now?.() ?? Date.now(), action });
    // The invite itself arrives over signaling; just make sure we're listening.
    this.deps.ensureEngineStarted();
  }
}

/** The slice of CallEngine the auto-accept/decline needs (structural). */
export interface AutoAcceptEngine {
  readonly state: ReadableStore<CallEngineState>;
  accept(): void;
  decline(): void;
}

/** The slice of `window` the auto-accept needs (hash handoff from native). */
export interface AutoAcceptWindow {
  location: { hash: string; pathname: string; search: string };
  history: { replaceState(data: unknown, unused: string, url?: string): void };
  addEventListener(type: 'hashchange', listener: () => void): void;
  removeEventListener(type: 'hashchange', listener: () => void): void;
}

/**
 * Completes a notification/native decision without a second ask: when the
 * pending invite carries action 'accept' or 'decline' and the engine has (or
 * receives) the MATCHING ringing incoming call, engine.accept()/engine.decline()
 * fires and the pending invite clears. The callId match means a stale decision
 * can never act on a different, later call.
 *
 * Also consumes hash changes: a warm app (activity relaunched via
 * onNewIntent → applyInviteHash, or the SW's decline fallback link) sets
 * location.hash on the live page, which the mount-time consumeLocation has
 * already missed. Returns a teardown.
 */
export function installAutoAccept(
  pickup: PendingInvitePickup,
  engine: AutoAcceptEngine,
  win: AutoAcceptWindow = window,
  now: () => number = Date.now,
): () => void {
  const tryAct = (): void => {
    const pending = pickup.state.get();
    if (!pending || pending.action === null) return;
    const s = engine.state.get();
    // Dead invite (ghost decision): the call ended before we could act — the
    // decision must not linger and must never act on anything else.
    if (s.lastEnded !== null && s.lastEnded.callId === pending.callId) {
      pickup.clear();
      return;
    }
    // Stale decision: past the server's 45 s ring window the invite cannot
    // still be answerable; a later same-callId state is out of contract.
    if (now() - pending.receivedAt > TIMEOUTS.RING_MS) {
      pickup.clear();
      return;
    }
    const call = s.call;
    if (
      call !== null &&
      call.phase === 'ringing' &&
      call.direction === 'incoming' &&
      call.callId === pending.callId
    ) {
      const action = pending.action;
      pickup.clear();
      if (action === 'accept') engine.accept();
      else engine.decline();
    }
  };
  const onHashChange = (): void => {
    pickup.consumeLocation(win.location, win.history);
  };

  const unsubs = [pickup.state.subscribe(tryAct), engine.state.subscribe(tryAct)];
  win.addEventListener('hashchange', onHashChange);
  tryAct(); // the invite may already be ringing when this installs

  return () => {
    for (const u of unsubs) u();
    win.removeEventListener('hashchange', onHashChange);
  };
}
